/**
 * Modern Bold · <mb-design-swatches> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * Enhancement for the design chips above the PDP size picker
 * (snippets/modern-design-swatches.liquid). The chips are plain links.
 *   • whole-rug preview card on hover (short intent delay) or keyboard
 *     focus, fine pointers only; a link to that design; opens above the row
 *     so it never covers the size picker; hoverable and closes on Escape
 *     (WCAG 1.4.13)
 *   • prev/next buttons when the row overflows (fine pointers only)
 *   • the row keeps title order, so the current design can sit anywhere:
 *     the row (never the page) scrolls to it on load
 *   • "+N" moves the next 50 chips out of an inert <template>
 *   • GA4 `design_swatch_click` (own name, apart from the carousel's
 *     `product_siblings_click`), same params
 */
if (!customElements.get('mb-design-swatches')) {
  // Long enough that a pointer crossing the row never opens the card.
  const HOVER_INTENT_MS = 150;
  const HIDE_DELAY_MS = 150;
  const PREVIEW_GAP = 8;
  const PAGE_SIZE = 50;

  class MBDesignSwatches extends HTMLElement {
    #cleanups = [];
    #track = null;
    #chips = [];
    #preview = null;
    #previewImg = null;
    #previewChip = null;
    #showTimer = 0;
    #hideTimer = 0;
    #finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    #updateNav = () => {};

    connectedCallback() {
      this.#track = this.querySelector('[data-design-track]');
      this.#chips = Array.from(this.querySelectorAll('[data-design-chip]'));
      this.#preview = this.querySelector('[data-design-preview]');
      this.#previewImg = this.querySelector('[data-design-preview-img]');
      if (!this.#track || !this.#chips.length) return;

      this.#chips.forEach((chip) => this.#bindChip(chip));
      this.#bindHost();
      this.#bindPreview();
      this.#bindNav();
      this.#bindMore();
      this.#bindTracking();
      this.#revealCurrent();
    }

    disconnectedCallback() {
      clearTimeout(this.#showTimer);
      clearTimeout(this.#hideTimer);
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    #on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.#cleanups.push(() => target.removeEventListener(type, fn, opts));
    }

    #bindChip(chip) {
      this.#on(chip, 'pointerenter', () => {
        if (!this.#finePointer.matches) return;
        clearTimeout(this.#showTimer);
        clearTimeout(this.#hideTimer);
        // With the card already open, moving along the row switches at once.
        if (!this.#preview.hidden) this.#showPreview(chip);
        else this.#showTimer = setTimeout(() => this.#showPreview(chip), HOVER_INTENT_MS);
      });
      this.#on(chip, 'pointerleave', () => clearTimeout(this.#showTimer));
      this.#on(chip, 'focus', () => {
        if (this.#finePointer.matches && chip.matches(':focus-visible')) this.#showPreview(chip);
      });
    }

    #bindHost() {
      // The current design, "+N" and the arrows have no card: hovering them
      // closes the last one. Delegated, because "+N" is replaced on load.
      this.#on(this, 'pointerover', (e) => {
        if (!e.target.closest('.is-current, [data-design-more], [data-design-prev], [data-design-next]')) return;
        this.#hidePreview();
      });

      // The card is a child of this element, so moving onto it never leaves
      // the host: that is what keeps it hoverable.
      this.#on(this, 'pointerenter', () => clearTimeout(this.#hideTimer));
      this.#on(this, 'pointerleave', () => {
        clearTimeout(this.#showTimer);
        this.#hideTimer = setTimeout(() => this.#reset(), HIDE_DELAY_MS);
      });
      // Focus may move from a chip onto the open card link; only leaving both
      // closes it.
      const onFocusOut = (e) => {
        if (this.#track.contains(e.relatedTarget) || this.#preview.contains(e.relatedTarget)) return;
        if (!this.matches(':hover')) this.#reset();
      };
      this.#on(this.#track, 'focusout', onFocusOut);
      this.#on(this.#preview, 'focusout', onFocusOut);
      this.#on(window, 'pageshow', (e) => {
        if (e.persisted) this.#reset();
      });
    }

    // Back to the keyboard-focused chip's card if there is one, else closed.
    #reset() {
      const focused = this.#track.querySelector('[data-design-chip]:focus-visible');
      if (focused && this.#finePointer.matches) this.#showPreview(focused);
      else this.#hidePreview();
    }

    #bindPreview() {
      const settle = () => this.#previewImg.classList.remove('is-loading');
      this.#on(this.#previewImg, 'load', settle);
      this.#on(this.#previewImg, 'error', settle);
      this.#on(document, 'keydown', (e) => {
        if (e.key !== 'Escape' || this.#preview.hidden) return;
        // Hand focus back to the chip before hiding the card that holds it.
        if (this.#preview.contains(document.activeElement)) this.#previewChip?.focus();
        this.#hidePreview();
      });
      // Tabbing scrolls the row to the focused chip: follow it. A card opened
      // by the pointer closes on scroll.
      this.#on(this.#track, 'scroll', () => {
        const chip = this.#previewChip;
        if (chip && chip === document.activeElement && chip.matches(':focus-visible')) this.#positionPreview();
        else this.#hidePreview();
      }, { passive: true });
    }

    #showPreview(chip) {
      clearTimeout(this.#showTimer);
      clearTimeout(this.#hideTimer);
      if (!chip.dataset.previewSrc) {
        this.#hidePreview();
        return;
      }
      this.#previewChip = chip;
      this.#preview.setAttribute('href', chip.getAttribute('href'));
      // Blank frame until the new rug loads, never the previous rug.
      if (this.#previewImg.getAttribute('src') !== chip.dataset.previewSrc) {
        this.#previewImg.classList.add('is-loading');
        this.#previewImg.src = chip.dataset.previewSrc;
      }
      this.querySelector('[data-design-preview-name]').textContent = chip.dataset.name;
      this.querySelector('[data-design-preview-colors]').textContent = chip.dataset.colors;
      this.querySelector('[data-design-preview-status]').hidden = !chip.hasAttribute('data-sold-out');
      this.#preview.hidden = false;
      this.#positionPreview();
    }

    // Above the row (over the title and price) so the card never covers the
    // size picker; below only when .mb-pdp-layout, which clips at its top
    // edge, or the viewport leaves no room.
    #positionPreview() {
      const chip = this.#previewChip;
      if (!chip) return;
      const host = this.getBoundingClientRect();
      const box = chip.getBoundingClientRect();
      const width = this.#preview.offsetWidth;
      const height = this.#preview.offsetHeight;

      const center = box.left + box.width / 2 - host.left;
      const left = Math.min(Math.max(center - width / 2, 0), Math.max(host.width - width, 0));

      const layoutTop = this.closest('.mb-pdp-layout')?.getBoundingClientRect().top ?? 0;
      const fitsAbove = host.top - PREVIEW_GAP - height >= Math.max(layoutTop, 0);
      const top = fitsAbove ? -PREVIEW_GAP - height : box.bottom - host.top + PREVIEW_GAP;

      this.#preview.style.left = `${Math.round(left)}px`;
      this.#preview.style.top = `${Math.round(top)}px`;
    }

    #hidePreview() {
      clearTimeout(this.#showTimer);
      clearTimeout(this.#hideTimer);
      this.#preview.hidden = true;
      this.#previewChip = null;
    }

    #bindNav() {
      const prev = this.querySelector('[data-design-prev]');
      const next = this.querySelector('[data-design-next]');
      if (!prev || !next) return;

      // aria-disabled, not disabled: the button may hold focus when the row
      // hits its end, and a natively disabled button drops focus to <body>.
      const update = () => {
        const t = this.#track;
        const show = t.scrollWidth > t.clientWidth + 1 && this.#finePointer.matches;
        if (!show && (document.activeElement === prev || document.activeElement === next)) {
          this.#chips[0].focus({ preventScroll: true });
        }
        prev.hidden = !show;
        next.hidden = !show;
        prev.setAttribute('aria-disabled', String(t.scrollLeft <= 1));
        next.setAttribute('aria-disabled', String(t.scrollLeft + t.clientWidth >= t.scrollWidth - 1));
      };

      const step = (button, dir) => {
        if (button.getAttribute('aria-disabled') === 'true') return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.#track.scrollBy({ left: dir * this.#track.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
      };

      this.#on(prev, 'click', () => step(prev, -1));
      this.#on(next, 'click', () => step(next, 1));
      this.#on(this.#track, 'scroll', update, { passive: true });
      this.#on(this.#finePointer, 'change', update);

      const ro = new ResizeObserver(update);
      ro.observe(this.#track);
      this.#cleanups.push(() => ro.disconnect());
      this.#updateNav = update;
    }

    #bindMore() {
      const rest = this.querySelector('[data-design-rest]');
      if (!rest) return;
      this.#on(this, 'click', (e) => {
        const button = e.target.closest('[data-design-more]');
        if (!button) return;
        const items = Array.from(rest.content.children).slice(0, PAGE_SIZE);
        const slot = button.closest('li');
        slot.before(...items);
        const added = items.map((li) => li.querySelector('[data-design-chip]')).filter(Boolean);
        added.forEach((chip) => this.#bindChip(chip));
        this.#chips.push(...added);
        // The button may be about to go: hand focus to the first new chip.
        added[0]?.focus();
        const left = rest.content.children.length;
        if (left) {
          button.textContent = `+${left}`;
          button.setAttribute('aria-label', `Show ${left} more designs`);
        } else {
          slot.remove();
        }
        this.#updateNav();
      });
    }

    // Scroll the row (never the page) so the current design is in view.
    #revealCurrent() {
      const current = this.#track.querySelector('.is-current');
      if (!current) return;
      const t = this.#track.getBoundingClientRect();
      const c = current.getBoundingClientRect();
      if (c.left >= t.left && c.right <= t.right) return;
      this.#track.scrollLeft += c.left - t.left - (t.width - c.width) / 2;
    }

    #bindTracking() {
      this.#on(this, 'click', (e) => {
        const fromCard = Boolean(e.target.closest('[data-design-preview]'));
        const chip = fromCard ? this.#previewChip : e.target.closest('[data-design-chip]');
        if (!chip || typeof window.gtag !== 'function') return;
        window.gtag('event', 'design_swatch_click', {
          clicked_element: fromCard ? 'design_preview' : 'design_swatch',
          from_product_id: this.dataset.fromId || '',
          from_product_handle: this.dataset.fromHandle || '',
          from_product_title: this.dataset.fromTitle || '',
          to_product_id: chip.dataset.productId || '',
          to_product_handle: chip.dataset.productHandle || '',
          to_product_title: chip.dataset.productTitle || '',
          to_product_url: chip.getAttribute('href') || '',
        });
      });
    }
  }

  customElements.define('mb-design-swatches', MBDesignSwatches);
}
